/**
 * 用户详情。
 *
 * 页面以「用户 × 梦灵」关系为排障中心，同时收拢账号身份、授权与登录会话。
 * 关系仍是会话、记忆和灵力的隔离锚点，底层不会与账号实体合表。
 *
 * 「重要事件」与「灵力流水」都是空表——RelationshipEvent 只记录非消息触发的
 * 重要事件（升级、形态切换、人工调整），逐条消息的关系值变化记在 Message 上。
 */

import { useState } from "react";
import { Alert, Card, Col, Descriptions, Row, Space, Statistic, Table, Tabs, Tag, Typography } from "antd";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { api } from "../../services/api";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, fmtNumber, RelativeTime, StatusTag, Tags } from "../../components/format";
import {
  PowerGrantModal,
  PowerGrantPlus,
  type PowerGrantTarget,
} from "../../components/PowerGrant";

interface RelationshipDetail {
  id: string;
  userId: string;
  userName: string | null;
  userStatus: string;
  userIdentities: { provider: string; masked: string }[];
  account: {
    id: string;
    status: string;
    preferences: unknown;
    featureNotices: {
      chatImagePowerAcknowledged: boolean;
      messageVoicePowerAcknowledged: boolean;
    };
    createdAt: string | null;
    lastActiveAt: string | null;
    deletionRequestedAt: string | null;
    deletedAt: string | null;
    profile: {
      displayName: string;
      gender: string | null;
      birthday: string | null;
      homeCity: string | null;
      avatarUrl: string | null;
    } | null;
    identities: {
      id: string;
      provider: string;
      masked: string;
      verifiedAt: string | null;
      verifiedSource: string | null;
    }[];
    consents: {
      kind: string;
      granted: boolean;
      policyVersion: number;
      grantedAt: string | null;
      revokedAt: string | null;
    }[];
    roles: {
      key: string;
      displayName: string;
      grantedAt: string | null;
      grantedBy: string | null;
    }[];
    sessions: {
      id: string;
      hostKind: string;
      issuedAt: string | null;
      lastUsedAt: string | null;
      expiresAt: string | null;
      revokedAt: string | null;
      revokedReason: string | null;
    }[];
  };
  characterName: string;
  status: string;
  startedAt: string | null;
  lastInteractionAt: string | null;
  state: {
    bond: number;
    level: number;
    characterAffinity: number;
    userPreferredName: string | null;
    selfIntroduction: string | null;
    userWish: string | null;
    updatedAt: string | null;
  } | null;
  wallet: {
    power: number;
    lifetimeGranted: number;
    lifetimePurchased: number;
    lifetimeRefunded: number;
    lastDailyGrantAt: string | null;
  } | null;
  counts: Record<string, number>;
  events: {
    id: string;
    kind: string;
    bondDelta: number | null;
    characterAffinityDelta: number | null;
    sourceType: string;
    policyVersion: number;
    createdAt: string | null;
  }[];
  quotas: { quotaKey: string; usedCount: number; periodStart: string | null; resetAt: string | null }[];
  transactions: {
    id: string;
    amount: number;
    balanceAfter: number;
    reason: string;
    skillKey: string | null;
    createdAt: string | null;
  }[];
}

export function RelationshipDetailPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [grantTarget, setGrantTarget] = useState<PowerGrantTarget | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ["relationship", id],
    queryFn: () => api.get<RelationshipDetail | null>(`/api/v1/admin/relationships/${id}`),
  });

  if (error) return <Alert type="error" showIcon message={(error as Error).message} />;
  if (!isLoading && !data) return <Alert type="warning" showIcon message="用户不存在" />;

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <Card title="用户" loading={isLoading} styles={{ body: { padding: 16 } }}>
        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={4}>
            <Statistic title="羁绊" value={data?.state?.bond ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="等级" value={data?.state?.level ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic
              title="好感"
              value={data?.state?.characterAffinity ?? 0}
              valueStyle={{ color: (data?.state?.characterAffinity ?? 0) > 1000 ? "#B4232E" : undefined }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Statistic
              title="灵力余额"
              value={data?.wallet?.power ?? 0}
              suffix={data ? (
                <PowerGrantPlus
                  onClick={() => setGrantTarget({
                    userId: data.userId,
                    userName: data.userName,
                    relationshipId: data.id,
                    characterName: data.characterName,
                    power: data.wallet?.power ?? 0,
                  })}
                />
              ) : null}
            />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="消息" value={data?.counts?.["messages"] ?? 0} />
          </Col>
          <Col xs={12} md={4}>
            <Statistic title="中枢决策" value={data?.counts?.["brainRuns"] ?? 0} />
          </Col>
        </Row>

        <Descriptions size="small" column={3} bordered>
          <Descriptions.Item label="用户">
            {data?.userName || "(未命名)"}
          </Descriptions.Item>
          <Descriptions.Item label="用户 ID">
            <Typography.Text copyable style={{ fontFamily: "monospace", fontSize: 12 }}>
              {data?.account.id}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="账号状态">
            <StatusTag value={data?.account.status} />
          </Descriptions.Item>
          <Descriptions.Item label="角色">{data?.characterName}</Descriptions.Item>
          <Descriptions.Item label="关系状态">
            <StatusTag value={data?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="称谓">
            {data?.state?.userPreferredName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="身份">
            {data?.userIdentities.map((i) => `${i.provider}:${i.masked}`).join("  ") || "—"}
          </Descriptions.Item>
          <Descriptions.Item label="管理角色">
            {data?.account.roles.length
              ? data.account.roles.map((role) => (
                  <Tag key={role.key} color={role.key === "super_admin" ? "red" : "blue"}>
                    {role.displayName}
                  </Tag>
                ))
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="功能提醒" span={3}>
            <Space wrap>
              <Tag color={data?.account.featureNotices.chatImagePowerAcknowledged ? "green" : "default"}>
                图片扣费提醒：
                {data?.account.featureNotices.chatImagePowerAcknowledged ? "已确认" : "未确认"}
              </Tag>
              <Tag color={data?.account.featureNotices.messageVoicePowerAcknowledged ? "green" : "default"}>
                语音扣费提醒：
                {data?.account.featureNotices.messageVoicePowerAcknowledged ? "已确认" : "未确认"}
              </Tag>
              <Typography.Text type="secondary">
                仅表示用户确认过提醒，不代表已使用过功能。
              </Typography.Text>
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="注册">{fmtDateTime(data?.account.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="最后活跃">{fmtDateTime(data?.account.lastActiveAt)}</Descriptions.Item>
          <Descriptions.Item label="建立于">{fmtDateTime(data?.startedAt)}</Descriptions.Item>
          <Descriptions.Item label="最后互动">{fmtDateTime(data?.lastInteractionAt)}</Descriptions.Item>
          <Descriptions.Item label="累计赠送 / 充值">
            {fmtNumber(data?.wallet?.lifetimeGranted)} / {fmtNumber(data?.wallet?.lifetimePurchased)}
          </Descriptions.Item>
          <Descriptions.Item label="自我介绍" span={3}>
            {data?.state?.selfIntroduction ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="对角色的期望" span={3}>
            {data?.state?.userWish ?? "—"}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Tabs
        items={[
          {
            key: "identity",
            label: "身份与授权",
            children: (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Card styles={{ body: { padding: 12 } }}>
                  <Descriptions size="small" column={3} bordered>
                    <Descriptions.Item label="性别">{data?.account.profile?.gender ?? "—"}</Descriptions.Item>
                    <Descriptions.Item label="常驻城市">
                      {data?.account.profile?.homeCity ?? "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="生日">
                      {data?.account.profile?.birthday?.slice(0, 10) ?? "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="注销" span={3}>
                      {data?.account.deletedAt
                        ? fmtDateTime(data.account.deletedAt)
                        : data?.account.deletionRequestedAt
                          ? `申请于 ${fmtDateTime(data.account.deletionRequestedAt)}`
                          : "—"}
                    </Descriptions.Item>
                    <Descriptions.Item label="界面偏好" span={3}>
                      <span className="mono">{JSON.stringify(data?.account.preferences ?? {})}</span>
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                <Row gutter={[12, 12]}>
                  <Col xs={24} lg={12}>
                    <Card title="登录身份（已脱敏）" size="small">
                      <Table
                        size="small"
                        rowKey="id"
                        pagination={false}
                        dataSource={data?.account.identities ?? []}
                        columns={[
                          { title: "渠道", dataIndex: "provider", width: 130 },
                          { title: "标识", dataIndex: "masked" },
                          {
                            title: "验证",
                            dataIndex: "verifiedAt",
                            width: 180,
                            render: (
                              value: string | null,
                              row: RelationshipDetail["account"]["identities"][number],
                            ) => value
                              ? `${fmtDateTime(value)}（${row.verifiedSource ?? "—"}）`
                              : "未验证",
                          },
                        ]}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Card title="授权同意" size="small">
                      <Table
                        size="small"
                        rowKey="kind"
                        pagination={false}
                        locale={{ emptyText: "尚无授权记录" }}
                        dataSource={data?.account.consents ?? []}
                        columns={[
                          { title: "类型", dataIndex: "kind" },
                          {
                            title: "状态",
                            dataIndex: "granted",
                            width: 90,
                            render: (value: boolean) => (
                              <Tag color={value ? "green" : "default"}>
                                {value ? "已授权" : "已撤回"}
                              </Tag>
                            ),
                          },
                          { title: "策略版本", dataIndex: "policyVersion", width: 90 },
                          { title: "时间", dataIndex: "grantedAt", render: fmtDateTime },
                        ]}
                      />
                    </Card>
                  </Col>
                </Row>
              </Space>
            ),
          },
          {
            key: "sessions",
            label: `登录会话 ${data?.account.sessions.length ?? 0}`,
            children: (
              <Card styles={{ body: { padding: 12 } }}>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  locale={{ emptyText: "尚无登录会话" }}
                  dataSource={data?.account.sessions ?? []}
                  columns={[
                    {
                      title: "ID",
                      dataIndex: "id",
                      width: 110,
                      render: (value: string) => value.slice(-8),
                    },
                    { title: "宿主", dataIndex: "hostKind", width: 130 },
                    { title: "签发", dataIndex: "issuedAt", width: 170, render: fmtDateTime },
                    { title: "最后使用", dataIndex: "lastUsedAt", width: 170, render: fmtDateTime },
                    { title: "过期", dataIndex: "expiresAt", width: 170, render: fmtDateTime },
                    {
                      title: "吊销",
                      dataIndex: "revokedAt",
                      render: (
                        value: string | null,
                        row: RelationshipDetail["account"]["sessions"][number],
                      ) => value ? `${fmtDateTime(value)} ${row.revokedReason ?? ""}` : "—",
                    },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "conversations",
            label: `会话 ${data?.counts?.["conversations"] ?? 0}`,
            children: (
              <DataTable
                path="/api/v1/admin/conversations"
                fixedParams={{ relationshipId: id }}
                columns={[
                  {
                    title: "会话",
                    dataIndex: "id",
                    width: 90,
                    render: (v: string) => <Link to={`/conversations/${v}`}>{v.slice(-8)}</Link>,
                  },
                  {
                    title: "标题",
                    dataIndex: "memoryTitle",
                    render: (v) => v || <span style={{ color: "#999" }}>未总结</span>,
                  },
                  { title: "摘要", dataIndex: "memorySummary", render: (v) => v || "—" },
                  { title: "消息", dataIndex: "messageCount", width: 80, align: "right" },
                  {
                    title: "好感变化",
                    dataIndex: "affinityDelta",
                    width: 100,
                    align: "right",
                    render: (v: number | null) => (v === null ? "—" : v > 0 ? `+${v}` : v),
                  },
                  {
                    title: "最后消息",
                    dataIndex: "lastMessageAt",
                    width: 130,
                    render: (v) => <RelativeTime value={v} />,
                  },
                ]}
              />
            ),
          },
          {
            key: "diaries",
            label: `日记 ${data?.counts?.["diaries"] ?? 0}`,
            children: (
              <DataTable
                path="/api/v1/admin/diaries/relationship"
                fixedParams={{ relationshipId: id }}
                columns={[
                  { title: "日期", dataIndex: "date", width: 110 },
                  { title: "标题", dataIndex: "title", width: 180, render: (v) => v || "—" },
                  { title: "摘要", dataIndex: "summary", render: (v) => v || "—" },
                  { title: "标签", dataIndex: "tags", width: 200, render: (v: string[]) => <Tags values={v} /> },
                  { title: "好感快照", dataIndex: "characterAffinitySnapshot", width: 100, align: "right" },
                  { title: "消息", dataIndex: "messageCount", width: 80, align: "right" },
                ]}
              />
            ),
          },
          {
            key: "memories",
            label: `会话摘要 ${data?.counts?.["memories"] ?? 0}`,
            children: (
              <DataTable
                path="/api/v1/admin/memories/conversation"
                fixedParams={{ relationshipId: id }}
                columns={[
                  { title: "标题", dataIndex: "title", width: 180, render: (v) => v || "—" },
                  { title: "摘要", dataIndex: "summary", render: (v) => v || "—" },
                  { title: "标签", dataIndex: "tags", width: 180, render: (v: string[]) => <Tags values={v} /> },
                  { title: "梦境标签", dataIndex: "dreamTags", width: 160, render: (v: string[]) => <Tags values={v} /> },
                  { title: "好感增量", dataIndex: "characterAffinityDelta", width: 90, align: "right" },
                  { title: "开始", dataIndex: "startedAt", width: 130, render: (v) => <RelativeTime value={v} /> },
                ]}
              />
            ),
          },
          {
            key: "brain",
            label: `中枢决策 ${data?.counts?.["brainRuns"] ?? 0}`,
            children: (
              <DataTable
                path="/api/v1/admin/brain/runs"
                fixedParams={{ relationshipId: id }}
                columns={[
                  {
                    title: "决策",
                    dataIndex: "id",
                    width: 90,
                    render: (v: string) => <Link to={`/brain/runs/${v}`}>{v.slice(-8)}</Link>,
                  },
                  { title: "形态", dataIndex: "formKey", width: 100, render: (v) => <StatusTag value={v} /> },
                  { title: "状态", dataIndex: "status", width: 90, render: (v) => <StatusTag value={v} /> },
                  { title: "安全", dataIndex: "safetyVerdict", width: 90, render: (v) => <StatusTag value={v} /> },
                  { title: "输入 token", dataIndex: "totalInputTokens", width: 100, align: "right", render: fmtNumber },
                  { title: "输出 token", dataIndex: "totalOutputTokens", width: 100, align: "right", render: fmtNumber },
                  { title: "延迟", dataIndex: "latencyMs", width: 80, align: "right", render: (v: number) => `${v}ms` },
                  { title: "时间", dataIndex: "createdAt", width: 130, render: (v) => <RelativeTime value={v} /> },
                ]}
              />
            ),
          },
          {
            key: "events",
            label: `重要事件 ${data?.events.length ?? 0}`,
            children: (
              <Card styles={{ body: { padding: 12 } }}>
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  RelationshipEvent 只记录非消息触发的重要事件（升级、形态切换、人工调整、迁移）。
                  逐条消息的羁绊与好感变化记在 Message 上——按每天 3 万条算，逐条建事件表一年就是 1,100 万行噪音。
                </Typography.Paragraph>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={data?.events ?? []}
                  locale={{ emptyText: "暂无重要事件" }}
                  columns={[
                    { title: "类型", dataIndex: "kind", width: 140 },
                    { title: "羁绊增量", dataIndex: "bondDelta", width: 100, align: "right" },
                    { title: "好感增量", dataIndex: "characterAffinityDelta", width: 100, align: "right" },
                    { title: "来源", dataIndex: "sourceType", width: 130 },
                    { title: "策略版本", dataIndex: "policyVersion", width: 90 },
                    { title: "时间", dataIndex: "createdAt", render: fmtDateTime },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "wallet",
            label: "灵力流水",
            children: (
              <Card styles={{ body: { padding: 12 } }}>
                <Table
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={data?.transactions ?? []}
                  locale={{ emptyText: "暂无灵力流水（ResourceTransaction 表为空）" }}
                  columns={[
                    {
                      title: "变动",
                      dataIndex: "amount",
                      width: 100,
                      align: "right",
                      render: (v: number) => (
                        <span style={{ color: v >= 0 ? "#2F7A4B" : "#B4232E" }}>
                          {v > 0 ? `+${v}` : v}
                        </span>
                      ),
                    },
                    { title: "变动后余额", dataIndex: "balanceAfter", width: 110, align: "right" },
                    { title: "原因", dataIndex: "reason", width: 150 },
                    { title: "Skill", dataIndex: "skillKey", width: 150, render: (v) => v || "—" },
                    { title: "时间", dataIndex: "createdAt", render: fmtDateTime },
                  ]}
                />
              </Card>
            ),
          },
          {
            key: "quota",
            label: "每日配额",
            children: (
              <Card styles={{ body: { padding: 12 } }}>
                <Table
                  size="small"
                  rowKey={(r: RelationshipDetail["quotas"][number]) => `${r.quotaKey}-${r.periodStart}`}
                  pagination={false}
                  dataSource={data?.quotas ?? []}
                  locale={{ emptyText: "暂无配额记录（DailyQuotaUsage 表为空）" }}
                  columns={[
                    { title: "配额键", dataIndex: "quotaKey", width: 200 },
                    { title: "已用", dataIndex: "usedCount", width: 100, align: "right" },
                    { title: "周期开始", dataIndex: "periodStart", render: fmtDateTime },
                    { title: "重置于", dataIndex: "resetAt", render: fmtDateTime },
                  ]}
                />
              </Card>
            ),
          },
        ]}
      />

      <PowerGrantModal
        target={grantTarget}
        onClose={() => setGrantTarget(null)}
        onGranted={() => {
          void queryClient.invalidateQueries({ queryKey: ["relationship", id] });
          void queryClient.invalidateQueries({ queryKey: ["/api/v1/admin/relationships"] });
        }}
      />
    </Space>
  );
}
